"use client"

import { useState } from "react"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { motion, AnimatePresence } from "framer-motion"
import {
  Monitor,
  Smartphone,
  Megaphone,
  Bot,
  Briefcase,
  Network,
  Shield,
  CheckCircle,
  ArrowRight,
  Users,
} from "lucide-react"
import Corevalues from "../components/CoreSolutions"

export default function Solution() {
  const [formData, setFormData] = useState({
    startupName: "",
    founderName: "",
    email: "",
    phoneNumber: "",
    service: [],
  })

  const [quoteAmounts, setQuoteAmounts] = useState({})
  const [errors, setErrors] = useState({})

  const services = [
    {
      name: "Web Development",
      icon: <Monitor className="w-5 h-5" />,
      description: "Custom websites and web applications",
    },
    {
      name: "App Development",
      icon: <Smartphone className="w-5 h-5" />,
      description: "iOS and Android mobile applications",
    },
    {
      name: "Digital Marketing",
      icon: <Megaphone className="w-5 h-5" />,
      description: "SEO, social media, and online advertising",
    },
    {
      name: "Automation",
      icon: <Bot className="w-5 h-5" />,
      description: "Process automation and workflow optimization",
    },
    {
      name: "AI Solution",
      icon: <Briefcase className="w-5 h-5" />,
      description: "Machine learning and AI integration",
    },
    {
      name: "ERP Solution",
      icon: <Network className="w-5 h-5" />,
      description: "Enterprise resource planning systems",
    },
  ]

  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === "founderName" && /\d/.test(value)) return
    if (name === "phoneNumber" && /[^\d]/.test(value)) return

    setFormData((prev) => ({ ...prev, [name]: value }))

    if (name === "email") {
      if (!value.includes(".com")) {
        setErrors((prev) => ({ ...prev, email: "Email must include .com" }))
      } else {
        const { email, ...rest } = errors
        setErrors(rest)
      }
    }
  }

  const handleServiceSelect = (serviceName) => {
    setFormData((prevState) => {
      const updatedServices = prevState.service.includes(serviceName)
        ? prevState.service.filter((s) => s !== serviceName)
        : [...prevState.service, serviceName]
      return { ...prevState, service: updatedServices }
    })
  }

  const handleQuoteChange = (e, service) => {
    setQuoteAmounts({ ...quoteAmounts, [service]: e.target.value })
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.startupName) newErrors.startupName = "Startup Name is required"

    if (!formData.founderName) newErrors.founderName = "Founder Name is required"

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Valid Email is required"
    else if (!formData.email.includes(".com")) newErrors.email = "Email must include .com"

    if (!formData.phoneNumber || !/^\d{10}$/.test(formData.phoneNumber))
      newErrors.phoneNumber = "Enter a valid 10-digit phone number"

    if (formData.service.length === 0) newErrors.service = "Select at least one service"

    formData.service.forEach((serviceName) => {
      const amount = quoteAmounts[serviceName]
      if (!amount || isNaN(amount) || Number(amount) <= 0) {
        newErrors[`quote_${serviceName}`] = `Enter a valid amount for ${serviceName}`
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validateForm()) {
      try {
        const servicesWithQuotes = formData.service.map((serviceName) => ({
          name: serviceName,
          quote: quoteAmounts[serviceName] || 0,
        }))

        const payload = {
          ...formData,
          services: servicesWithQuotes,
        }

        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/solutions`, payload)

        if (response.status === 201) {
          toast.success("Form Submitted Successfully!")
          setFormData({
            startupName: "",
            founderName: "",
            email: "",
            phoneNumber: "",
            service: [],
          })
          setQuoteAmounts({})
          setErrors({})
        }
      } catch (error) {
        console.error("Error submitting form:", error)
        toast.error("Failed to submit the form. Please try again.")
      }
    } else {
      toast.error("Please fill the form correctly before submitting.")
    }
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center"
          >
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 mb-8">
              <Shield className="w-4 h-4 text-[#fd3e3d]" />
              <span className="text-sm font-medium text-gray-700">Trusted by 500+ Startups</span>
            </div>

            <h1 className="text-3xl sm:text-3xl lg:text-2xl font-bold text-gray-900 leading-tight mb-3">
              At TACTOS, we safeguard{" "}
              <span className="text-[#fd3e3d]">
                startups and entrepreneurs
              </span>{" "}
              from unreliable tech solution providers
            </h1>

            <p className="text-md md:text-md text-gray-600 max-w-4xl mx-auto mb-6 leading-relaxed">
              Acting as a trusted intermediary, we connect you with vetted and reliable tech partners. Just share your
              budget—we'll ensure your project is delivered on time, within budget, and without compromise.
            </p>

            <div className="flex items-center justify-center gap-8 text-sm text-gray-600 mb-8">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#fd3e3d]" />
                <span>No hidden fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#fd3e3d]" />
                <span>No commissions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#fd3e3d]" />
                <span>Dependable support</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-md mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">500+</div>
                <div className="text-sm text-gray-600">Projects Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">98%</div>
                <div className="text-sm text-gray-600">Success Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">24/7</div>
                <div className="text-sm text-gray-600">Support</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Core Values Section */}
      <Corevalues />

      {/* Form Section */}
      <div className="relative container md:px-4 sm:px-6 pb-20 mt-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden -mt-20 relative z-10"
        >
          {/* Form Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 py-4 text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Get Your Project Quote</h2>
            <p className="text-gray-300">Provide your budget and we'll connect you with the right partners</p>
          </div>

          <form onSubmit={handleSubmit} className="px-8 py-4">
            {/* Contact Information */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#fd3e3d]" />
                Contact Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  { field: "startupName", label: "Startup Name", placeholder: "Enter your startup name" },
                  { field: "founderName", label: "Founder Name", placeholder: "Enter founder's name" },
                  { field: "email", label: "Email Address", placeholder: "founder@startup.com" },
                  { field: "phoneNumber", label: "Phone Number", placeholder: "Enter 10-digit number" },
                ].map(({ field, label, placeholder }) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                    <input
                      type="text"
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      placeholder={placeholder}
                      className={`w-full px-2 py-1 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors[field] ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-gray-400"
                        }`}
                    />
                    {errors[field] && (
                      <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-600 rounded-full" />
                        {errors[field]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Services Selection */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#fd3e3d]" />
                Select Services & Budget
              </h3>

              {errors.service && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errors.service}</p>
                </div>
              )}

              {/* Grid for two columns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {services.map((service, index) => {
                  const isSelected = formData.service.includes(service.name);
                  return (
                    <motion.div
                      key={index}
                      layout
                      className={`border rounded-xl p-4 transition-all ${isSelected ? "border-blue-300 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                        }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex items-center pt-1">
                          <input
                            type="checkbox"
                            id={`service-${index}`}
                            checked={isSelected}
                            onChange={() => handleServiceSelect(service.name)}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div className="flex-1">
                          <label htmlFor={`service-${index}`} className="flex items-center gap-3 cursor-pointer">
                            <div className="p-2 bg-white text-[#fd3e3d] rounded-lg border border-gray-200">{service.icon}</div>
                            <div>
                              <h4 className="font-medium text-gray-900">{service.name}</h4>
                              <p className="text-sm text-gray-600">{service.description}</p>
                            </div>
                          </label>

                          <AnimatePresence>
                            {isSelected && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="mt-2 pl-11"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Budget (₹)</label>
                                    <input
                                      type="number"
                                      min="0"
                                      inputMode="numeric"
                                      onWheel={(e) => e.target.blur()}
                                      onKeyDown={(e) => {
                                        if (["-", "e", "E"].includes(e.key)) {
                                          e.preventDefault();
                                        }
                                      }}
                                      value={quoteAmounts[service.name] || ""}
                                      onChange={(e) => handleQuoteChange(e, service.name)}
                                      placeholder="Enter your budget"
                                      className={`w-full px-2 py-1 border rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors[`quote_${service.name}`] ? "border-red-300 bg-red-50" : "border-gray-300"
                                        }`}
                                    />
                                    {errors[`quote_${service.name}`] && (
                                      <p className="text-sm text-red-600 mt-1">{errors[`quote_${service.name}`]}</p>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-4 py-2 bg-black rounded-full text-white font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Submit Project Details
                <ArrowRight className="w-4 h-4 text-[#fd3e3d]" />
              </button>
              <p className="text-sm text-gray-600 mt-3">
                We'll get back to you within 24 hours with vetted partner recommendations
              </p>
            </div>
          </form>
        </motion.div>
      </div>


      <ToastContainer
        position="top-center"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  )
}
